import { useCallback } from 'react';
import { useAuthStore } from '@/store/authStore';

/**
 * Hook for authentication state and actions
 * Provides a convenient interface for auth operations across the app
 *
 * NOTE: This hook does NOT call checkAuth() automatically.
 * Authentication initialization is the sole responsibility of AuthProvider,
 * which calls checkAuth() once on mount with proper guards.
 * Having this hook also call checkAuth() caused race conditions with
 * coordinatedRefresh() leading to infinite retry loops (WP-BUG-XX).
 */
export function useAuth() {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    mfaRequired,
    mfaToken,
    login,
    register,
    logout,
    verifyMfa,
    requestMagicLink,
    verifyMagicLink,
    requestPasswordReset,
    resetPassword,
    checkAuth,
    clearError,
    clearMfaState,
    setUser,
  } = useAuthStore();

  const handleLogin = useCallback(
    async (email: string, password: string) => {
      return login(email, password);
    },
    [login]
  );

  const handleRegister = useCallback(
    async (email: string, name: string, gdprConsent: boolean) => {
      return register(email, name, gdprConsent);
    },
    [register]
  );

  const handleLogout = useCallback(async () => {
    return logout();
  }, [logout]);

  const handleVerifyMfa = useCallback(
    async (token: string, code: string) => {
      return verifyMfa(token, code);
    },
    [verifyMfa]
  );

  const handleRequestMagicLink = useCallback(
    async (email: string) => {
      return requestMagicLink(email);
    },
    [requestMagicLink]
  );

  const handleVerifyMagicLink = useCallback(
    async (token: string) => {
      return verifyMagicLink(token);
    },
    [verifyMagicLink]
  );

  const handleRequestPasswordReset = useCallback(
    async (email: string) => {
      return requestPasswordReset(email);
    },
    [requestPasswordReset]
  );

  const handleResetPassword = useCallback(
    async (token: string, password: string) => {
      return resetPassword(token, password);
    },
    [resetPassword]
  );

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    error,
    mfaRequired,
    mfaToken,

    // Actions
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    verifyMfa: handleVerifyMfa,
    requestMagicLink: handleRequestMagicLink,
    verifyMagicLink: handleVerifyMagicLink,
    requestPasswordReset: handleRequestPasswordReset,
    resetPassword: handleResetPassword,
    checkAuth,
    clearError,
    clearMfaState,
    setUser,
  };
}

export default useAuth;
